import React, { type MouseEvent } from 'react';

import { useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import CrossIcon from '@atlaskit/icon/core/migration/close--cross';
import MediaServicesActualSizeIcon from '@atlaskit/icon/core/migration/grow-diagonal--media-services-actual-size';
import MediaServicesFitToPageIcon from '@atlaskit/icon/core/migration/shrink-diagonal--media-services-fit-to-page';
import { Box, xcss } from '@atlaskit/primitives';
import { N0, N800 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';

import { ModalMode } from '../../../common/utils/analytics/types';

import { DockIcon } from './assets/dock-icon';
import { messages } from './messages';

type Props = {
	title: string;
	mode: ModalMode;
	analyticsSource: string;
	onClose: () => void;
	onModeChange: (mode: ModalMode) => void;
	onPrevModeChange?: () => void;
};

export const ModalHeader = ({
	title,
	mode,
	analyticsSource,
	onClose,
	onModeChange,
	onPrevModeChange,
}: Props) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleHeaderClick = () => {
		if (mode !== ModalMode.DOCKED) {
			return;
		}
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: analyticsSource,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'videoModalHeaderButton',
			},
		}).fire();

		onPrevModeChange?.();
	};

	const handleClose = (e: MouseEvent) => {
		e.stopPropagation();

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: analyticsSource,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'videoModalCloseButton',
			},
		}).fire();

		onClose();
	};
	//
	const handleDockClick = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: analyticsSource,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'videoModalDockButton',
			},
		}).fire();

		onModeChange(ModalMode.DOCKED);
	};

	const handleExpandClick = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: analyticsSource,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'videoModalExpandButton',
			},
		}).fire();

		onModeChange(ModalMode.EXPANDED);
	};

	const handleMinimizeClick = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: analyticsSource,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'videoModalMinimizeButton',
			},
		}).fire();

		onModeChange(ModalMode.DEFAULT);
	};

	const isDocked = mode === ModalMode.DOCKED;

	return (
		<Box
			testId="luna-video-player-modal.ui.content.header.container"
			onClick={handleHeaderClick}
			xcss={[headerStyles, isDocked && dockedHeaderStyles]}
		>
			<Box testId="luna-video-player-modal.ui.content.header.title" xcss={titleStyles}>
				{title}
			</Box>
			{mode !== ModalMode.DOCKED && (
				<Button
					testId="luna-video-player-modal.ui.content.header.button-dock"
					spacing="none"
					label={formatMessage(messages.dock)}
					appearance="subtle"
					iconBefore={<DockIcon label="" size="medium" primaryColor={token('color.icon', N800)} />}
					onClick={handleDockClick}
				/>
			)}
			{mode === ModalMode.DEFAULT && (
				<Button
					testId="luna-video-player-modal.ui.content.header.button-expand"
					spacing="none"
					label={formatMessage(messages.expand)}
					appearance="subtle"
					iconBefore={
						<MediaServicesActualSizeIcon
							label="video-player-modal.expand-icon"
							LEGACY_size="medium"
							color={token('color.icon', N800)}
							spacing="spacious"
						/>
					}
					onClick={handleExpandClick}
				/>
			)}
			{mode === ModalMode.EXPANDED && (
				<Button
					testId="luna-video-player-modal.ui.content.header.button-minimize"
					spacing="none"
					label={formatMessage(messages.minimize)}
					appearance="subtle"
					iconBefore={
						<MediaServicesFitToPageIcon
							label="video-player-modal.minimize-icon"
							LEGACY_size="medium"
							color={token('color.icon', N800)}
							spacing="spacious"
						/>
					}
					onClick={handleMinimizeClick}
				/>
			)}
			<Button
				testId="luna-video-player-modal.ui.content.header.button-close"
				spacing="none"
				appearance="subtle"
				label={formatMessage(messages.close)}
				iconBefore={
					<CrossIcon
						label="video-player-modal.cross-icon"
						LEGACY_size="medium"
						color={token('color.icon', N0)}
						spacing="spacious"
					/>
				}
				onClick={handleClose}
			/>
		</Box>
	);
};

const headerStyles = xcss({
	background: token('elevation.surface'),
	height: '40px',
	boxSizing: 'border-box',
	padding: 'space.050',
	display: 'flex',
	alignItems: 'center',
	gap: 'space.100',
});

const dockedHeaderStyles = xcss({
	cursor: 'pointer',
});

const titleStyles = xcss({
	fontWeight: 600,
	fontSize: '12px',
	lineHeight: '16px',
	color: 'color.text',
	marginRight: 'auto',
	padding: 'space.100',
});
